<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { REGISTER_TYPE_CHARACTER_LIMIT, VISIT_SITE_LABEL } from "@/components/ExperiencePage/Sessions/constants.ts";
import ProgramDates from "@/components/ProgramDates";
import { PROGRAM_SESSION_TERMS_DISPLAY_MAP } from "@/components/program-manager/sessions/constants.js";
import ApplyButton from "@/components/shared/Button/ApplyButton.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { hasConfigurableRegistrationEnabled } from "@/composables/authorization.js";
import { REGISTER_TYPE } from "@/constants.js";
import { buildStringDateRangeSessionTravel, formatDate } from "@/mixins/helpers";
import { formattedPriceFromCentsWithoutCents as formattedPrice } from "@/util/formatter";
import { pastSessionDeadline } from "@/util/pastSessionDeadline";
import { hidePrice as hidePriceInfo } from "@/util/pricingLabel";

const store = useStore();
const emit = defineEmits(["dategridrow-set-session-id", "applyBtnClicked"]);
const props = defineProps({
  session: {
    type: Object,
    default() {
      return {};
    },
  },
  indexNumber: {
    type: Number,
    default: 0,
  },
  oddrow: {
    type: Boolean,
    default: false, // Changes the BG Color of columns 2 & 3
  },
  selectedSessionId: {
    type: String,
    default: "",
  },
  displayStartingPrice: {
    type: Boolean,
    default: true,
  },
  externalLink: {
    type: String,
    default: "",
  },
  registerType: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const bgClass = computed(() => {
  return props.oddrow ? "bg-gray-30" : "bg-white";
});

const billingRules = computed(() => store.state.billingRules);

const hidePrice = computed(() => hidePriceInfo(props?.session, billingRules.value));

const sessionTerms = computed(() => {
  return props.session?.terms?.map((term) => term.name) ?? [];
});

const getDisplayNameForTerms = computed(() => {
  const normalizedTerms = [...sessionTerms.value].sort();
  const termKey = normalizedTerms.join(" + ");
  return PROGRAM_SESSION_TERMS_DISPLAY_MAP[termKey] || normalizedTerms.join(" + ");
});

const getDisplayDates = computed(() => {
  return buildStringDateRangeSessionTravel(props.session?.session_travel_details, {
    academic_start_date: props.session?.academic_start_date,
    academic_end_date: props.session?.academic_end_date,
  });
});

// Read off the first travel-detail row, null or absent — counts as not final.
const isStartDateFinal = computed(() => Boolean(props.session?.session_travel_details?.[0]?.arrival_date_is_final));
const isEndDateFinal = computed(() => Boolean(props.session?.session_travel_details?.[0]?.departure_date_is_final));

const getFormattedPrice = computed(() => {
  return formattedPrice(props.session?.base_price_in_cents);
});

const displayDeadline = computed(() => {
  return props.session?.application_deadline;
});

const getFormattedDeadline = computed(() => {
  return formatDate(props.session?.application_deadline);
});

const radioButtonId = computed(() => {
  return "dates-radio-" + props.indexNumber;
});

const isSelectedSession = computed(() => {
  return props.selectedSessionId === props.session?.id ?? "";
});

const disableButton = computed(() => {
  return !props.session?.application_deadline || pastSessionDeadline(props.session);
});

const overrideColumnDisplay = computed(() => {
  if (!isSelectedSession.value) return {};
  return {
    display: "block !important",
  };
});

const deadlinesText = computed(() => {
  if (props.registerType === REGISTER_TYPE.PERSONALIZE || props.registerType === REGISTER_TYPE.REGISTER)
    return "Registration Deadline";
  else return "Application Deadline";
});

const customRegisterType = computed(() => {
  if (hasConfigurableRegistrationEnabled.value && props.registerType.length > REGISTER_TYPE_CHARACTER_LIMIT) {
    return VISIT_SITE_LABEL;
  }
  return props.registerType;
});

const screenReaderLabel = computed(() => {
  return `Date number ${props.indexNumber + 1}: ${getDisplayDates.value}`;
});

const screenReaderLabelId = computed(() => {
  return radioButtonId.value + "-label";
});
</script>

<template>
  <div
    :class="isSelectedSession ? `border ${primaryColorBorder}` : 'cursor-pointer'"
    class="grid grid-cols-1 lg:grid-cols-3 font-montserrat"
    data-id="dates-grid"
    @click.stop="emit('dategridrow-set-session-id', props.session?.id)"
  >
    <span class="sr-only text-black text-xl" :id="screenReaderLabelId">{{ screenReaderLabel }}</span>
    <div :class="isSelectedSession ? `${primaryColorClassBG}` : bgClass" class="pt-4 pb-4 pr-2 pl-2">
      <div class="grid grid-cols-date-radios">
        <input
          :id="radioButtonId"
          type="radio"
          class="w-5 h-5 relative inset-y-29percent inset-x-27percent custom-date-radiobutton"
          name="session-date-grid-option"
          :checked="isSelectedSession"
          :value="props.session?.id"
          :aria-labelledby="screenReaderLabelId"
          @mousedown.prevent
        />

        <!-- Term(s) & Dates -->
        <div :class="isSelectedSession ? 'text-white' : 'text-university-primary'" class="ml-4 flex flex-col">
          <SkeletonLoader v-if="isLoading" rounded />
          <span v-else-if="sessionTerms" :id="`grid-session-term-${props.session?.id}`" class="font-normal text-sm">
            {{ getDisplayNameForTerms }}
          </span>

          <SkeletonLoader v-if="isLoading" class="mt-2" rounded />
          <ProgramDates
            v-else-if="props.session?.session_travel_details"
            :id="`api-session-dates-${props.session?.id}`"
            :dates="getDisplayDates"
            :start-is-final="isStartDateFinal"
            :end-is-final="isEndDateFinal"
            tag="div"
            class="mt-2"
            reverse-icon-order
            experience-card-icon
            text-class="font-bold text-base uppercase"
          />
        </div>
      </div>
    </div>
    <div :class="bgClass" class="pt-4 pb-4 pr-2 pl-5">
      <div class="flex flex-col">
        <SkeletonLoader v-if="isLoading" rounded />
        <span
          v-else-if="props.session?.base_price_in_cents && !hidePrice && props.displayStartingPrice"
          :id="`grid-program-price-${props.session?.id}`"
          :class="`uppercase ${secondaryColorClass} font-bold`"
        >
          Starting at {{ getFormattedPrice }}
        </span>
        <SkeletonLoader v-if="isLoading" class="mt-2" rounded />
        <div
          v-else-if="displayDeadline"
          :id="`grid-program-deadline-${props.session?.id}`"
          class="flex justify-start items-center mt-2"
        >
          <img src="/images/icon-calendar.svg" class="object-contain" width="24" height="24" alt="Calendar icon" />
          <div class="text-sm ml-2 text-university-primary">
            <p>
              {{ deadlinesText }}:
              <span class="font-bold text-university-primary uppercase">
                {{ getFormattedDeadline }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div :class="bgClass" class="pt-4 pb-4 pr-4 pl-4 lg:pl-2 hidden lg:pr-2 lg:block" :style="overrideColumnDisplay">
      <div v-if="isSelectedSession" class="flex justify-center justify-items-center">
        <SkeletonLoader v-if="isLoading" rounded />
        <ApplyButton
          v-else
          :id="`card-apply-btn-${props.session?.id}`"
          :disabled="disableButton"
          class="h-60p w-full xs:w-60r lg:w-16r border-transparent"
          event-category="Program Page Personalize CTA"
          show-arrow
          :external-link="props.externalLink"
          :register-type="customRegisterType"
          @click="emit('applyBtnClicked')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-date-radiobutton {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: #fff;
  border: 2px solid rgb(0, 0, 0);
  border-radius: 50%;
}

.custom-date-radiobutton:checked {
  background-color: #fff;
  border-color: rgb(0, 0, 0);
  border: 1px solid rgb(0, 0, 0);
}

.custom-date-radiobutton:checked::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 85%;
  height: 85%;
  background-color: #fff;
  border: 2px solid rgb(0, 0, 0);
  border-radius: 50%;
}
</style>
